import { internalApiSlice, transformResponse } from "@redux/api/internalApiSlice";
import { PaymentTerms } from "@/types/general";
import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "@redux/hooks";

export const paymentTermsApi = internalApiSlice.injectEndpoints({
    endpoints: (builder) => ({
        getPaymentTerms: builder.query<PaymentTerms.Root[], void>({
            query: () => ({
                url: `payment-terms`
            }),
            providesTags: [{ type: "PaymentTerms", id: "LIST" }],
            transformResponse
        }),

        getModulePaymentTerms: builder.query<PaymentTerms.Root[], string>({
            query: (module) => ({
                url: `${module}/payment-terms`
            }),
            providesTags: [{ type: "PaymentTerms", id: "LIST" }],
            transformResponse
        }),

        // Note: mutations invalidate the whole list instead of patching the cache locally, because
        // sending "is_default: true" makes the backend unset that flag on every other term of the company.
        createPaymentTerms: builder.mutation<PaymentTerms.Root, PaymentTerms.DTO.CreateUpdate>({
            query: (body) => ({
                url: `payment-terms`,
                method: "POST",
                data: body
            }),
            transformResponse,
            invalidatesTags: [{ type: "PaymentTerms", id: "LIST" }]
        }),

        updatePaymentTerms: builder.mutation<PaymentTerms.Root, PaymentTerms.DTO.CreateUpdate & { id: number }>({
            query: ({ id, ...body }) => ({
                url: `payment-terms/${id}`,
                method: "PUT",
                data: body
            }),
            transformResponse,
            invalidatesTags: [{ type: "PaymentTerms", id: "LIST" }]
        }),

        deletePaymentTerms: builder.mutation<void, number>({
            query: (id) => ({
                url: `payment-terms/${id}`,
                method: "DELETE"
            }),
            invalidatesTags: [{ type: "PaymentTerms", id: "LIST" }]
        })
    })
});

const selectPaymentTermsResult = paymentTermsApi.endpoints.getPaymentTerms.select();

const selectAllPaymentTerms = createSelector(
    selectPaymentTermsResult,
    (paymentTerms) => paymentTerms?.data ?? []
)

export const selectPaymentTermsById = createSelector(
    selectAllPaymentTerms,
    (state: RootState, pt_id: number | undefined) => pt_id,
    (paymentTerms, pt_id) => pt_id != undefined ? paymentTerms.find(pt => pt.id === pt_id) : undefined
);

export const {
    useGetPaymentTermsQuery,
    useGetModulePaymentTermsQuery,
    useCreatePaymentTermsMutation,
    useUpdatePaymentTermsMutation,
    useDeletePaymentTermsMutation
} = paymentTermsApi;
